<template>
    <div :class="container_class">
        <template>
            <label class="vs-input--label" :class="label_class" v-if="label">{{ label }}</label> 
            <v-select 
                :class="['select-foreign-control', multiple ? 'select-foreign--multiple' : 'select-foreign--single', input_class]" 
                @search="use_cache ? () => null : do_search" 
                @input="updateModel"
                :options="value_options" 
                v-model="value"
                :filterable="true" 
                :multiple="multiple"
                :placeholder="initialising ? 'Loading...' : placeholder"
                :label="label_keys?label_keys[0]:'label'"
                :disabled="disabled"
                :closeOnSelect="close_on_select"
                :append-to-body="append_to_body"
            >
                <template slot="selected-option" scope="option">
                    <span class="selected-option-label">
                        <template v-for="(lk, i) in label_keys">
                            <span v-if="option[lk]" :key="i"><template v-if="label_separator && i > 0"> {{ label_separator }} </template>{{ option[lk] }}<template v-if="!label_separator">&nbsp;</template></span>
                        </template>
                        <template v-if="show_id_in_label && option.id">
                            &nbsp;(ID: {{ option.id }})
                        </template>
                    </span>
                </template>
                <template v-slot:option="option">
                    <div class="select-foreign-option">
                        <span v-if="multiple" class="checkbox-wrapper option-checkbox-wrapper">
                            <input 
                                type="checkbox" 
                                :checked="isOptionSelected(option)"
                                class="select-foreign-checkbox"
                                readonly
                                tabindex="-1"
                            />
                        </span>
                        <span class="option-label">
                            <template v-for="(lk, i) in label_keys">
                                <span v-if="option[lk]" :key="i"><template v-if="label_separator && i > 0"> {{ label_separator }} </template>{{ option[lk] }}<template v-if="!label_separator">&nbsp;</template></span>
                            </template>
                            <template v-if="show_id_in_label && option.id">
                                &nbsp;(ID: {{ option.id }})
                            </template>
                        </span>
                    </div>
                </template>
            </v-select>
            <div v-if="warning" class="text-danger mt-2 mb-3">
                <small>{{ warning_text }}</small>
            </div>
        </template>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import { Request } from "../../../utils/requests"
import md5 from 'md5'
import moment from 'moment'

export default {
    props: {
        container_class: {
            required: false,
            default: 'vx-col w-full'
        },
        disabled: {
            required: false,
            default: false
        },
        label: {
            required: false,
            default: null
        },
        label_class: {
            required: false,
            default: 'mb-3'
        },
        input_class: {
            required: false,
            default: 'mt-3 w-full'
        },
        model: {
            required: true
        },
        placeholder: {
            required: false,
            default: 'Type to Search..'
        },
        warning: {
            required: false,
            default: false
        },
        warning_text: {
            required: false,
            default: null
        },
        multiple: {
            default: false
        },
        endpoint: {
            required: true
        },
        list_length: {
            default: 1000
        },
        label_keys: {
            required: true,
            default: () => ['id', ]
        },
        show_id_in_label: {
            default: false
        },
        load_initial: {
            default: false
        },
        use_id_as_value: {
            default: false
        },
        data_key: {
            required: false,
            default: null
        },
        close_on_select: {
            required: false,
            type: Boolean,
            default: true
        },
        use_cache: {
            required: false,
            type: Boolean,
            default: false
        },
        autoselect_first: {
            type: Boolean,
            default: false,
        },
        append_to_body: {
            type: Boolean,
            default: false
        },
        label_separator: {
            required: false,
            default: ''
        },
        result_filter: {
            required: false,
            type: Function,
            default: null
        }
    },
    data() {
        return {
            value: this.model,
            value_options: [],

            initialising: false
        }
    },
    mounted() {
        this.user = this.model
        if(this.load_initial)
            this.do_search(null, ()=>{}, true)
    },
    components: {
        vSelect,
    },
    watch: {
        model(newV, oldV) {
            if(newV == oldV) return
            if(!this.multiple) {
                if(this.use_id_as_value)
                    this.value = this.value_options.find(v => v.id == newV) || newV
                else
                    this.value = newV
            } else {
                if(this.use_id_as_value) {
                    let x = []
                    if(!newV) newV = []
                    newV.forEach(v2 => {
                        x.push(this.value_options.find(v => v.id == v2) || v2)
                    })
                    this.value = x
                }
                else
                    this.value = newV
            }
        },
        load_initial(newV, oldV) {
            if (newV == oldV) return
            else if (newV) this.do_search(null, (val) => {this.initialising = val}, true)
        },
        endpoint(newV) {
            this.do_search(null, (val) => {this.initialising = val}, true)
        },
        value_options(newV) {
            // When options arrive, try to resolve the model value into a full object if we only have an ID
            if (newV && newV.length > 0 && this.use_id_as_value && this.model) {
                if (!this.multiple) {
                    const resolved = newV.find(v => v.id == this.model);
                    if (resolved) this.value = resolved;
                } else if (Array.isArray(this.model)) {
                    const resolvedMultiple = this.model.map(id => newV.find(v => v.id == id) || id);
                    this.value = resolvedMultiple;
                }
            }
        }
    },
    methods: {
        isOptionSelected(option) {
            if (!this.multiple || !this.value) return false;
            if (!Array.isArray(this.value)) return false;
            
            return this.value.some(selected => {
                if (this.use_id_as_value) {
                    return selected && selected.id === option.id;
                } else {
                    if (selected && option && selected.id && option.id) {
                        return selected.id === option.id;
                    }
                    return selected === option;
                }
            });
        },
        updateModel(newValue) {
            try {
                const valueToProcess = newValue !== undefined ? newValue : this.value;
                
                if(valueToProcess && this.use_id_as_value) {
                    if(!this.multiple)
                        this.$emit("update:model", valueToProcess.id)
                    else {
                        const ids = Array.isArray(valueToProcess) ? valueToProcess.map(function(v) { return v && v.id ? v.id : v; }) : [];
                        this.$emit("update:model", ids)
                    }
                } else {
                    if(this.multiple) {
                        let valueToEmit = [];
                        if (valueToProcess) {
                            if (Array.isArray(valueToProcess)) {
                                valueToEmit = valueToProcess.slice();
                            } else if (valueToProcess !== null && valueToProcess !== undefined) {
                                valueToEmit = [valueToProcess];
                            }
                        }
                        this.$emit("update:model", valueToEmit)
                    } else {
                        this.$emit("update:model", valueToProcess)
                    }
                }
                
                this.$emit('change')
            } catch(error) {
                const fallbackValue = newValue !== undefined ? newValue : this.value;
                this.$emit("update:model", fallbackValue)
            }
        },
        async do_search(search, loading, skip_search=false){

            if(this.endpoint.split('?').at(-1).includes(null)) return

            let limit = this.list_length
            //support for offline
            let key = md5(this.endpoint)

            if(this.use_cache) {
                limit = 1000
            }

            let local_data = await this.$ionicStorage.get(key)
            // localStorage.getItem(key)

            if(local_data && this.use_cache) {
                try {
                    local_data = JSON.parse(local_data)
    
                    //checking cache is valid or not
                    let cache_on = local_data.cache_timestamp
                    if (cache_on < moment(new Date()).subtract(6, 'hours').toISOString()) {
                        //cache is invalid
                        await this.$ionicStorage.remove(key)
    
                    } else {
                        this.value_options = local_data.data
                        return
                    }

                } catch(err) {
                    await this.$ionicStorage.remove(key)
                }
            }

            let data = {
                limit: limit
            }
            if(!skip_search)
                data['search'] = search
            
            loading(true)
            let url = this.endpoint
            
            Request(this, 'GET', url, data).then( 
                async (res) => {
                    // Handle different response structures
                    let results = null;
                    if (res && res.results && Array.isArray(res.results)) {
                        // Standard format: {count, results: [...]}
                        results = res.results;
                    } else if (res && res.data && res.data.results && Array.isArray(res.data.results)) {
                        // Nested format: {data: {results: [...]}}
                        results = res.data.results;
                    } else if (Array.isArray(res)) {
                        // Direct array response
                        results = res;
                    } else if (res && res.data && Array.isArray(res.data)) {
                        // Nested array: {data: [...]}
                        results = res.data;
                    } else {
                        results = [];
                    }

                    let filtered = this.data_key ? (results[this.data_key] || []) : results;
                    if (this.result_filter) filtered = filtered.filter(this.result_filter);
                    this.value_options = filtered;

                    if (
                        this.autoselect_first &&
                        this.load_initial &&
                        search == null &&
                        results.length > 0
                    ) {
                        this.value = results[0];
                        this.updateModel();
                    }

                    // setting cache
                    await this.$ionicStorage.set(key, JSON.stringify({cache_timestamp: moment(new Date()).toISOString(), data: results}))

                    loading(false)
                }, (err) => {
                    this.$vs.notify({
                        time: 2500,
                        title: 'Error',
                        text: 'An Error Occured. Please Try Again',
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                    this.value_options = []
                    loading(false)
                }
            )
            
        },
    }
}
</script>

<style scoped>
.select-foreign-option {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    padding: 4px 0;
    width: 100%;
}

.checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.option-checkbox-wrapper {
    margin-right: 10px;
    min-width: 18px;
    pointer-events: none;
}

.select-foreign-checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin: 0;
    accent-color: rgba(var(--vs-primary), 1);
    pointer-events: none;
}

.select-foreign-checkbox:checked {
    accent-color: rgba(var(--vs-primary), 1);
}

.option-label {
    flex: 1;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
}

.selected-option-label {
    display: inline-block;
    max-width: 100%;
    line-height: 1.3;
}

::v-deep .select-foreign-control .vs__dropdown-toggle {
    min-height: 44px;
    align-items: center;
}

::v-deep .select-foreign-control .vs__selected-options {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    padding: 2px 0;
    min-width: 0;
}

::v-deep .select-foreign-control .vs__selected {
    max-width: 100%;
    line-height: 1.3;
}

::v-deep .select-foreign--single .vs__selected-options {
    flex-wrap: nowrap;
}

::v-deep .select-foreign--single .vs__selected {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

::v-deep .select-foreign--single .selected-option-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

::v-deep .select-foreign--multiple .vs__dropdown-toggle {
    align-items: flex-start;
    padding-top: 2px;
    padding-bottom: 2px;
}

::v-deep .select-foreign--multiple .vs__selected-options {
    align-items: flex-start;
}

::v-deep .select-foreign--multiple .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

::v-deep .select-foreign--multiple .selected-option-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

::v-deep .select-foreign-control .vs__search,
::v-deep .select-foreign-control .vs__search::placeholder {
    white-space: nowrap;
}

::v-deep .select-foreign-control .vs__dropdown-menu {
    max-width: 100%;
}
</style>